import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useToast } from '@/hooks/use-toast'
import { Question, InterviewResult } from './InterviewContext'
import { isValidAccessCodeFormat } from '@/utils/accessCodeUtils'
import { API_BASE_URL } from '@/utils/apiConfig'

export interface Employee {
  id: string
  name: string
  mail_adresi: string
  department: string
  role: string
  createdAt: string
}

export interface TestAssignment {
  id: string
  testId: string
  test_id: string
  testName: string
  employeeId: string
  employee_id: string
  employeeName: string
  assignedAt: string
  assignment_start_date: string
  dueDate: string
  status:
    | 'completed'
    | 'pending'
    | 'abandoned'
    | 'expired'
    | 'timeout'
    | 'camera_access_error'
    | 'in_progress'
    | 'chunk_upload_error'
    | 'renewed'
    | 'speed_test_fail'
  resultId?: string
  accessCode?: string
  company_id?: string
}

export interface Test {
  id: string
  name: string
  description: string
  questionIds: string[]
  createdAt: string
  createdBy: string
  time_limit: number
}

interface EmployeeContextType {
  employees: Employee[]
  tests: Test[]
  assignments: TestAssignment[]
  companyId: string | null
  setCompanyId: (id: string | null) => void
  addEmployee: (employee: Omit<Employee, 'id' | 'createdAt'>) => void
  updateEmployee: (employee: Employee) => void
  deleteEmployee: (id: string) => void
  addTest: (test: Omit<Test, 'id' | 'createdAt'>) => void
  updateTest: (test: Test) => void
  deleteTest: (id: string) => void
  assignTest: (testId: string, employeeIds: string[], dueDate: string, accessCode?: string) => void
  completeAssignment: (assignmentId: string, resultId: string) => void
  getEmployeeTests: (employeeId: string) => TestAssignment[]
  getTestAssignments: (testId: string) => TestAssignment[]
  getEmployeeById: (id: string) => Promise<Employee | undefined>
  getTestById: (id: string) => Promise<Test | undefined>
  getAssignmentById: (id: string) => Promise<TestAssignment | undefined>
  verifyTestAccessCode: (
    code: string
  ) => Promise<{ valid: boolean; assignmentId?: string; candidateToken?: string }>
}

const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined)

export const useEmployee = () => {
  const context = useContext(EmployeeContext)
  if (!context) {
    throw new Error('useEmployee must be used within an EmployeeProvider')
  }
  return context
}

interface EmployeeProviderProps {
  children: ReactNode
}

export const EmployeeProvider = ({ children }: EmployeeProviderProps) => {
  const { toast } = useToast()

  const [employees, setEmployees] = useState<Employee[]>([])

  const [tests, setTests] = useState<Test[]>([])

  const [assignments, setAssignments] = useState<TestAssignment[]>([])

  const [companyId, setCompanyId] = useState<string | null>(null)

  const addEmployee = (employeeData: Omit<Employee, 'id' | 'createdAt'>) => {
    const newEmployee: Employee = {
      ...employeeData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
    }

    setEmployees((prev) => [...prev, newEmployee])
    toast({
      title: 'Çalışan Eklendi',
      description: `${newEmployee.name} başarıyla eklendi.`,
    })
  }

  const updateEmployee = (updatedEmployee: Employee) => {
    setEmployees((prev) =>
      prev.map((emp) => (emp.id === updatedEmployee.id ? updatedEmployee : emp))
    )
    toast({
      title: 'Çalışan Güncellendi',
      description: `${updatedEmployee.name} bilgileri güncellendi.`,
    })
  }

  const deleteEmployee = (id: string) => {
    const employeeToDelete = employees.find((e) => e.id === id)
    if (!employeeToDelete) return

    setEmployees((prev) => prev.filter((emp) => emp.id !== id))

    setAssignments((prev) => prev.filter((assign) => assign.employeeId !== id))

    toast({
      title: 'Çalışan Silindi',
      description: `${employeeToDelete.name} başarıyla silindi.`,
    })
  }

  const addTest = (testData: Omit<Test, 'id' | 'createdAt'>) => {
    const newTest: Test = {
      ...testData,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
    }

    setTests((prev) => [...prev, newTest])
    toast({
      title: 'Test Eklendi',
      description: `${newTest.name} başarıyla oluşturuldu.`,
    })
  }

  const updateTest = (updatedTest: Test) => {
    setTests((prev) => prev.map((test) => (test.id === updatedTest.id ? updatedTest : test)))

    if (updatedTest.name) {
      setAssignments((prev) =>
        prev.map((assign) =>
          assign.testId === updatedTest.id ? { ...assign, testName: updatedTest.name } : assign
        )
      )
    }

    toast({
      title: 'Test Güncellendi',
      description: `${updatedTest.name} güncellendi.`,
    })
  }

  const deleteTest = (id: string) => {
    const testToDelete = tests.find((t) => t.id === id)
    if (!testToDelete) return

    setTests((prev) => prev.filter((test) => test.id !== id))

    setAssignments((prev) => prev.filter((assign) => assign.testId !== id))

    toast({
      title: 'Test Silindi',
      description: `${testToDelete.name} başarıyla silindi.`,
    })
  }

  const assignTest = (
    testId: string,
    employeeIds: string[],
    dueDate: string,
    providedAccessCode?: string
  ) => {
    const test = tests.find((t) => t.id === testId)
    if (!test) {
      toast({
        title: 'Hata',
        description: 'Test bulunamadı.',
        variant: 'destructive',
      })
      return
    }

    const newAssignments = employeeIds
      .map((empId) => {
        const employee = employees.find((e) => e.id === empId)
        if (!employee) return null

        const accessCode = providedAccessCode || generateUniqueAccessCode()

        return {
          id: `${Date.now()}-${empId}`,
          testId,
          testName: test.name,
          employeeId: empId,
          employeeName: employee.name,
          assignedAt: new Date().toISOString(),
          dueDate,
          status: 'pending' as const,
          accessCode,
        }
      })
      .filter(Boolean) as TestAssignment[]

    setAssignments((prev) => [...prev, ...newAssignments])

    toast({
      title: 'Test Atandı',
      description: `${test.name} testi ${newAssignments.length} çalışana atandı.`,
    })
  }

  const completeAssignment = (assignmentId: string, resultId: string) => {
    setAssignments((prev) =>
      prev.map((assign) =>
        assign.id === assignmentId ? { ...assign, status: 'completed', resultId } : assign
      )
    )

    toast({
      title: 'Test Tamamlandı',
    })
  }

  const getEmployeeTests = (employeeId: string) => {
    return assignments.filter((assign) => assign.employeeId === employeeId)
  }

  const getTestAssignments = (testId: string) => {
    return assignments.filter((assign) => assign.testId === testId)
  }

  const getEmployeeById = async (id: string) => {
    if (!id) {
      console.warn('No ID provided to getTestById')
      return undefined
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/employee/${id}`)
      if (response.ok) {
        const data = await response.json()
        // Convert API field 'personel' to 'name'
        return {
          ...data,
          name: data.personel, // map 'personel' to 'name'
        }
      }
    } catch (error) {
      console.error(`Failed to fetch employee ${id}:`, error)
    }

    return undefined
  }

  const getTestById = async (id: string): Promise<Test | undefined> => {
    if (!id) {
      console.warn('No ID provided to getTestById')
      return undefined
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${id}`)

      if (response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.error(`Failed to fetch test ${id}:`, error)
    }

    return undefined // Default to undefined if not found, error, or non-ok response
  }

  const getAssignmentById = async (id: string): Promise<TestAssignment | undefined> => {
    if (!id) {
      console.warn('No ID provided to getAssignmentById')
      return undefined
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/assignments/${id}`) // Adjust URL if needed

      if (response.ok) {
        // HTTP 200-299
        const ass = await response.json()
        // return await response.json();
        return ass
      }
    } catch (error) {
      // Catch network errors or if response.json() fails
      console.error(`Failed to fetch assignment ${id}:`, error)
    }

    return undefined // Default to undefined if not found, error, or non-ok response
  }

  const generateUniqueAccessCode = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let result = ''
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }

    const codeExists = assignments.some((a) => a.accessCode === result)
    if (codeExists) {
      return generateUniqueAccessCode()
    }

    return result
  }

  const verifyTestAccessCode = async (
    code: string
  ): Promise<{ valid: boolean; assignmentId?: string; candidateToken?: string }> => {
    if (!isValidAccessCodeFormat(code)) return { valid: false }

    try {
      const response = await fetch(`${API_BASE_URL}/api/verify_access_code`, {
        // Adjust URL if needed
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessCode: code }),
      })

      if (response.ok) {
        const data = await response.json()

        if (data.valid && data.companyId) {
          setCompanyId(data.companyId) // 🔑 store for session
        }

        return {
          valid: data.valid === true, // Ensure boolean
          assignmentId: data.assignmentId,
          candidateToken: data.candidateToken,
        }
      }
    } catch (error) {
      // Catch network errors or if response.json() fails, etc.
      // console.error("Verification API call failed:", error); // Minimal logging
    }

    return { valid: false } // Default to invalid if any issue or non-ok response
  }

  const value = {
    employees,
    tests,
    assignments,
    companyId,
    setCompanyId,
    addEmployee,
    updateEmployee,
    deleteEmployee,
    addTest,
    updateTest,
    deleteTest,
    assignTest,
    completeAssignment,
    getEmployeeTests,
    getTestAssignments,
    getEmployeeById,
    getTestById,
    getAssignmentById,
    verifyTestAccessCode,
  }

  return <EmployeeContext.Provider value={value}>{children}</EmployeeContext.Provider>
}
